import React from "react";
import { useParams, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { CheckCircle2, ArrowRight, PhoneCall } from "lucide-react";
import { SERVICES, CERT_IMAGE } from "../data/mock";
import PageHeader from "../components/PageHeader";
import ContactCTA from "../components/ContactCTA";
import { ApproachSection } from "../components/Sections";

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) {
    return (
      <div className="lis-container py-32 text-center">
        <h1 className="text-2xl font-bold">Service not found</h1>
        <Link to="/services" className="btn-brand mt-6 inline-flex">Back to Services</Link>
      </div>
    );
  }

  const Icon = Icons[service.icon] || Icons.BadgeCheck;

  return (
    <>
      <PageHeader
        title={`${service.code}  ${service.title}`}
        subtitle={service.excerpt}
        crumbs={[{ label: "Services", to: "/services" }, { label: service.code }]}
      />

      <section className="section-pad bg-white">
        <div className="lis-container grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: `${service.color}15`, color: service.color }}>
                <Icon size={26} />
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{service.tag}</div>
                <div className="text-xl font-bold text-slate-900">{service.code}   {service.title}</div>
              </div>
            </div>

            <h2 className="mt-8 text-2xl font-bold text-slate-900">Overview</h2>
            <div className="divider-red mt-3" />
            <p className="mt-4 text-slate-600 leading-relaxed">
              {service.excerpt} Our consultants take you from a first orientation workshop, through gap analysis, documentation, implementation, internal audit, management review, and finally certification support with your chosen certifying body.
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Whether you're a fast-growing SME or a large enterprise, our engagement is tailored to your size, sector and risk profile. We deliver on fixed-fee, milestone-based commercials with clear timelines and ownership.
            </p>

            <h3 className="mt-10 text-xl font-bold text-slate-900">Key Benefits</h3>
            <div className="divider-red mt-3" />
            <ul className="mt-5 grid sm:grid-cols-2 gap-3">
              {service.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-slate-700">
                  <CheckCircle2 size={18} className="text-brand mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <h3 className="mt-10 text-xl font-bold text-slate-900">What's Included</h3>
            <div className="divider-red mt-3" />
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              {[
                "Awareness & leadership workshop",
                "Gap analysis report",
                "Documented information (manual, procedures, formats)",
                "Risk & opportunity register",
                "Implementation hand-holding",
                "Internal auditor training",
                "Internal audit & MRM support",
                "Certification audit (Stage 1 & 2) support",
              ].map((it) => (
                <div key={it} className="flex items-start gap-2 text-sm text-slate-700 p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <CheckCircle2 size={16} className="text-brand mt-0.5 flex-shrink-0" />
                  <span>{it}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/contact" className="btn-brand">Get a Free Quote <ArrowRight size={16} /></Link>
              <Link to="/services" className="btn-outline">See All Services</Link>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
              <img src={CERT_IMAGE} alt="Certification" className="w-full h-56 object-cover" />
            </div>
            <div className="rounded-2xl p-6 bg-slate-900 text-white">
              <div className="text-xs uppercase tracking-widest text-slate-400">Need help?</div>
              <h4 className="mt-1 text-xl font-bold">Speak to a senior consultant</h4>
              <p className="mt-2 text-sm text-slate-300">Get clarity on timelines, effort and pricing for {service.code} certification.</p>
              <a href="tel:+917982810821" className="btn-brand mt-5 w-full justify-center">
                <PhoneCall size={16} /> +91 79828 10821
              </a>
            </div>
            <div className="rounded-2xl p-6 border border-slate-100">
              <div className="text-xs uppercase tracking-widest text-slate-500">Other Standards</div>
              <ul className="mt-3 space-y-2">
                {SERVICES.filter((s) => s.slug !== service.slug).slice(0, 6).map((s) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="flex items-center justify-between gap-2 text-sm text-slate-700 hover:text-brand py-1.5">
                      <span>{s.code}  {s.title}</span>
                      <ArrowRight size={12} />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <ApproachSection />
      <ContactCTA />
    </>
  );
};

export default ServiceDetailPage;
